import type { MetricSnapshot } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { useFormatters } from "@/lib/format";

/**
 * ADP-121: the per-portfolio list row and the per-member campaign row share the
 * same MetricSnapshot column set, so the header labels and value cells live in
 * one place. Order matches the ADP-121 spec: Impr / Clicks / CTR / Spend / CPC /
 * Orders / Units / Sales / CVR / ACOS / ROAS / CPA.
 */
export const PORTFOLIO_METRIC_COLUMNS = [
  "impressions",
  "clicks",
  "ctr",
  "spend",
  "cpc",
  "orders",
  "units",
  "sales",
  "cvr",
  "acos",
  "roas",
  "cpa",
] as const satisfies readonly (keyof MetricSnapshot)[];

export function MetricHeaderCells(): React.ReactElement {
  const { t } = useTranslation("portfolios");
  return (
    <>
      {PORTFOLIO_METRIC_COLUMNS.map((key) => (
        <span key={key} className="text-right">
          {t(`columns.${key}`)}
        </span>
      ))}
    </>
  );
}

export function MetricValueCells({
  metrics,
  currencyCode = "USD",
}: {
  metrics: MetricSnapshot;
  /** ADP-110: the scope's reporting currency for the (possibly converted) sums. */
  currencyCode?: string;
}): React.ReactElement {
  const { formatCurrency, formatNumber, formatPercent } = useFormatters();

  const format = (key: keyof MetricSnapshot): string => {
    const value = metrics[key];
    switch (key) {
      case "spend":
      case "cpc":
      case "sales":
      case "cpa":
        return formatCurrency(value, currencyCode);
      case "ctr":
      case "cvr":
      case "acos":
        return formatPercent(value);
      case "roas":
        return `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`;
      default:
        return formatNumber(value, { maximumFractionDigits: 0 });
    }
  };

  return (
    <>
      {PORTFOLIO_METRIC_COLUMNS.map((key) => (
        <span key={key} className="text-right font-mono tabular-nums text-ink">
          {format(key)}
        </span>
      ))}
    </>
  );
}
